import { ColDef } from '@ag-grid-community/core';
import { CellClassParams, CellStyle } from '@ag-grid-community/core/dist/cjs/es5/entities/colDef';
import round from 'lodash/round';
import React from 'react';
import { NavLink } from 'react-router-dom';
import hexToRgba from 'hex-to-rgba';
import { agTableValueComparator } from '@creditsights/styleguide-web';
import { AG_TABLE_RIGHT_ALIGNED_COLUMN_DEF } from '../../../../Base.constants';
import { getCompanyPageLink } from '../../../../pages/Companies/Companies.logic';

import { DrillDownDto } from '../../../../services/dtos/riskProducts/drillDown.dto';
import { RiskDistributionGroupBy } from '../../../../services/dtos/riskProducts/riskDistribution.dto';
import { WIDGET_ID } from '../../../../services/dtos/riskProducts/widgetMetric.dto';
import { getColor } from '../../../../utils/chartUtils';
import { getCompanyTag } from '../../../../utils/tagUtils';
import MetricChangeIcon from '../../common/MetricChangeIcon/MetricChangeIcon';
import { formatAsPctString } from '../../common/MetricChangeIcon/MetricChangeIcon.logic';

export const DEFAULT_SORT_FIELD = 'name';
export const DEFAULT_SORT_ORDER = 'asc';

const cqsColumnDef: ColDef<DrillDownDto> = {
  colId: 'cqs',
  headerName: 'CQS',
  field: 'cqs',
  ...AG_TABLE_RIGHT_ALIGNED_COLUMN_DEF,
  cellRenderer: ({ data, valueFormatted }) => (
    <MetricChangeIcon
      displayValue={valueFormatted}
      value={data.cqs}
      previousValue={data.cqs_previous}
      dataTestIdPrefix="cqs-"
    />
  ),
  valueFormatter: (params) => {
    const value = params.data?.cqs;
    if (value == null) {
      return '';
    }
    return round(value, 0).toString();
  },
  width: 80,
  suppressMenu: true,
  resizable: false,
};

const creColumnDef: ColDef<DrillDownDto> = {
  colId: 'cre',
  headerName: 'CRE',
  field: 'cre',
  ...AG_TABLE_RIGHT_ALIGNED_COLUMN_DEF,
  cellRenderer: (params) => (
    <MetricChangeIcon
      displayValue={params.valueFormatted}
      value={params.data.cre_1y}
      previousValue={params.data.cre_previous}
      dataTestIdPrefix="cre-"
    />
  ),
  valueFormatter: (params) => {
    const value = params.data?.cre_1y;
    if (value == null) {
      return '';
    }
    return formatAsPctString(value);
  },
  width: 96,
  suppressMenu: true,
  resizable: false,
  comparator: (valueA, valueB, nodeA, nodeB) => {
    return agTableValueComparator(nodeA?.data?.cre_1y, nodeB?.data?.cre_1y);
  },
};

const commonDef: ColDef<DrillDownDto>[] = [
  {
    colId: 'name',
    field: 'name',
    headerName: 'Name',
    cellRenderer: (params) => (
      <NavLink to={getCompanyPageLink(getCompanyTag(params.data.cs_companyid))}>{params.data.name}</NavLink>
    ),
    comparator: agTableValueComparator,
    suppressMenu: true,
    resizable: false,
  },
  {
    colId: 'crl',
    headerName: 'CRL',
    field: 'credit_risk_level',
    width: 80,
    suppressMenu: true,
    resizable: false,
    cellStyle: (param: CellClassParams<DrillDownDto>): CellStyle | undefined => {
      const color = getColor(param.value);
      return color ? { backgroundColor: hexToRgba(color, 0.16) } : undefined;
    },
  },
  cqsColumnDef,
  creColumnDef,
];

const changeChartDef: ColDef<DrillDownDto>[] = [
  {
    colId: 'name',
    field: 'name',
    headerName: 'Name',
    cellRenderer: (params) => (
      <NavLink to={getCompanyPageLink(getCompanyTag(params.data.cs_companyid))}>{params.data.name}</NavLink>
    ),
    width: 329,
    comparator: agTableValueComparator,
    suppressMenu: true,
    resizable: false,
  },
  {
    colId: 'sector',
    field: 'model_sector',
    headerName: 'Sector',
    width: 172,
    suppressMenu: true,
    resizable: false,
  },
  {
    colId: 'rating',
    field: 'acr_level_4',
    headerName: 'Rating',
    width: 88,
    suppressMenu: true,
    resizable: false,
  },
];

const getChangeChartColumnDef = (metric: string | undefined): ColDef<DrillDownDto>[] => {
  const clonedChangeChartDef = [...changeChartDef];
  clonedChangeChartDef.splice(
    2,
    0,
    metric?.startsWith('cqs')
      ? {
          ...cqsColumnDef,
          width: 64,
        }
      : {
          ...creColumnDef,
          width: 64,
        }
  );
  return clonedChangeChartDef;
};

export const getDrillDownColumns = (
  groupBy?: RiskDistributionGroupBy,
  widget?: WIDGET_ID,
  metric?: string
): ColDef<DrillDownDto>[] => {
  const clonedCommonDef = [...commonDef];

  if (widget === WIDGET_ID.CHANGE_CHART) {
    return getChangeChartColumnDef(metric);
  }

  if (groupBy) {
    clonedCommonDef.splice(
      1,
      0,
      groupBy === RiskDistributionGroupBy.SECTOR
        ? {
            colId: 'sector',
            field: 'model_sector',
            headerName: 'Sector',
            width: 124,
            suppressMenu: true,
            resizable: false,
          }
        : {
            colId: 'rating',
            field: 'acr_level_4',
            headerName: 'Rating',
            width: 124,
            suppressMenu: true,
            resizable: false,
          }
    );
  }
  return clonedCommonDef;
};
